import { useGetAllBranchesQuery } from '../../Commerce/services/branches.service';
import { useGetCommerceSummariesUnscopedQuery } from '../../Commerce/services';
import type { Option } from '@/components/inputs/AutocompleteInput';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 50;
const BRANCHES_PAGE_SIZE = 1000;

interface UseCommerceScopeSearchParams {
  enabled: boolean;
  selectedCommerceIds: string[];
  excludedCommerceIds?: string[];
}

const useCommerceScopeSearch = ({
  enabled,
  selectedCommerceIds,
  excludedCommerceIds = [],
}: UseCommerceScopeSearchParams) => {
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [labelsById, setLabelsById] = useState<Record<string, string>>({});

  useEffect(() => {
    const timeout = setTimeout(
      () => setDebouncedSearch(inputValue.trim()),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timeout);
  }, [inputValue]);

  const { data: searchData, isFetching: isSearching } =
    useGetCommerceSummariesUnscopedQuery(
      { name: debouncedSearch, limit: SEARCH_LIMIT },
      { skip: !enabled }
    );

  const missingLabelIds = useMemo(
    () =>
      [...new Set([...selectedCommerceIds, ...excludedCommerceIds])].filter(
        (id) => !labelsById[id]
      ),
    [selectedCommerceIds, excludedCommerceIds, labelsById]
  );

  const { data: selectedData } = useGetCommerceSummariesUnscopedQuery(
    { ids: missingLabelIds },
    { skip: !enabled || missingLabelIds.length === 0 }
  );

  useEffect(() => {
    const items = [...(searchData?.items ?? []), ...(selectedData?.items ?? [])];
    if (items.length === 0) return;
    setLabelsById((previous) => {
      const pending = items.filter((item) => previous[item.id] !== item.name);
      if (pending.length === 0) return previous;
      const next = { ...previous };
      for (const item of pending) next[item.id] = item.name;
      return next;
    });
  }, [searchData, selectedData]);

  const options: Option[] = useMemo(
    () =>
      (searchData?.items ?? []).map((commerce) => ({
        label: commerce.name,
        data: commerce.id,
      })),
    [searchData]
  );

  const {
    data: branchesData,
    currentData: currentBranchesData,
    isFetching: isLoadingBranches,
  } = useGetAllBranchesQuery(
    {
      paginated: { page: 1, pageSize: BRANCHES_PAGE_SIZE },
      filters: { commerceIds: selectedCommerceIds },
    },
    { skip: !enabled || selectedCommerceIds.length === 0 }
  );

  const commerceIdByBranchIdRef = useRef(new Map<string, string>());
  for (const branch of branchesData?.items ?? []) {
    commerceIdByBranchIdRef.current.set(branch.id, branch.commerceId);
  }

  const branchesOptions: Option[] = useMemo(() => {
    if (selectedCommerceIds.length === 0) return [];
    return (branchesData?.items ?? []).map((branch) => ({
      label: branch.name,
      data: branch.id,
    }));
  }, [branchesData, selectedCommerceIds.length]);

  const allBranchIds: string[] | null = useMemo(() => {
    if (!currentBranchesData || selectedCommerceIds.length === 0) return null;
    if (currentBranchesData.items.length < currentBranchesData.count) {
      return null;
    }
    return currentBranchesData.items.map((branch) => branch.id);
  }, [currentBranchesData, selectedCommerceIds.length]);

  const getBranchCommerceId = useCallback(
    (branchId: string) => commerceIdByBranchIdRef.current.get(branchId),
    []
  );

  return {
    inputValue,
    setInputValue,
    options,
    labelsById,
    isSearching: isSearching || debouncedSearch !== inputValue.trim(),
    branchesOptions,
    allBranchIds,
    getBranchCommerceId,
    isLoadingBranches,
  };
};

export default useCommerceScopeSearch;
